import * as React from 'react'

import { listTagsApiV1TagsGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { useInfiniteSearchList } from '#/shared/lib/api/use-infinite-search-list'
import type { PageRequestParams } from '#/shared/lib/api/use-infinite-search-list'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

const TAGS_QUERY_KEY = ['tags', 'list'] as const

export const NEW_TAG_PREFIX = '__new__:'

export function isNewTagValue(value: string): boolean {
  return value.startsWith(NEW_TAG_PREFIX)
}

export function newTagName(value: string): string {
  return value.startsWith(NEW_TAG_PREFIX)
    ? value.slice(NEW_TAG_PREFIX.length)
    : value
}

export function newTagValue(name: string): string {
  return `${NEW_TAG_PREFIX}${name}`
}

function fetchTagsPage(params: PageRequestParams, signal?: AbortSignal) {
  return listTagsApiV1TagsGet(
    { q: params.q || null, page: params.page, size: params.size },
    signal,
  )
}

interface UseTagsListOptions {
  seedOptions?: MultiSelectOption[]
}

export function useTagsList({ seedOptions }: UseTagsListOptions = {}) {
  const [enabled, setEnabled] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [draftTags, setDraftTags] = React.useState<MultiSelectOption[]>([])

  const list = useInfiniteSearchList({
    queryKey: TAGS_QUERY_KEY,
    fetcher: fetchTagsPage,
    query: searchValue,
    enabled,
  })

  const options: MultiSelectOption[] = React.useMemo(() => {
    const fetched = list.items.map((tag) => ({
      value: tag.id,
      label: tag.name,
    }))
    const fetchedIds = new Set(fetched.map((o) => o.value))
    const seedExtra = (seedOptions ?? []).filter(
      (o) => !fetchedIds.has(o.value),
    )
    return [...draftTags, ...seedExtra, ...fetched]
  }, [list.items, seedOptions, draftTags])

  const createDraftTag = React.useCallback((name: string): string => {
    const value = newTagValue(name)
    setDraftTags((prev) =>
      prev.some((tag) => tag.value === value)
        ? prev
        : [{ value, label: name, accent: 'new' }, ...prev],
    )
    return value
  }, [])

  const onOpenChange = React.useCallback((open: boolean) => {
    if (open) setEnabled(true)
  }, [])

  return {
    options,
    searchValue,
    onSearchChange: setSearchValue,
    isLoading: list.isLoading,
    isFetchingMore: list.isFetchingMore,
    hasMore: list.hasMore,
    onLoadMore: list.loadMore,
    onOpenChange,
    createDraftTag,
  }
}
